import { Link, Navigate, useParams } from "react-router-dom";
import { CATEGORIES, POSTS, SITE } from "@/mocks/gossip";

export default function PostDetail() {
  const { slug } = useParams<{ slug: string }>();
  const post = POSTS.find((p) => p.slug === slug);

  if (!post) {
    return <Navigate to="/" replace />;
  }

  const related = POSTS.filter(
    (p) => p.slug !== post.slug && p.category === post.category
  ).slice(0, 3);
  const fallback = POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);
  const rec = related.length ? related : fallback;

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: post.title,
    description: post.summary,
    datePublished: `2026-${post.date.replace("-", "-")}`,
    image: post.cover,
    inLanguage: "zh-CN",
    author: { "@type": "Person", name: post.author },
    publisher: { "@type": "Organization", name: SITE.name },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://199695.cn/post/${post.slug}`,
    },
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://199695.cn/" },
      { "@type": "ListItem", position: 2, name: post.category, item: "https://199695.cn/" },
      { "@type": "ListItem", position: 3, name: post.title },
    ],
  };

  return (
    <article className="mx-auto max-w-7xl px-4 md:px-6 pt-28 md:pt-36 pb-14 md:pb-20">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }}
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* 主内容 */}
        <div className="lg:col-span-8">
          {/* 面包屑 */}
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500 mb-5">
            <Link to="/" className="hover:text-primary-600 transition">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-primary-600">{post.category}</span>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-400 truncate max-w-[200px]">{post.title}</span>
          </nav>

          <header>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="text-xs font-medium text-primary-600 bg-primary-100 rounded-md px-2.5 py-1">{post.category}</span>
              <span className="text-xs text-foreground-400 flex items-center gap-1"><i className="ri-price-tag-3-line"></i>{post.tag}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 leading-tight">
              {post.title}
            </h1>
            <p className="mt-4 text-base md:text-lg text-foreground-600 leading-relaxed border-l-4 border-accent-400 pl-4">
              {post.summary}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-400">
              <span className="flex items-center gap-1.5">
                <span className="w-7 h-7 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700"><i className="ri-pencil-ruler-line"></i></span>
                {post.author}
              </span>
              <span className="flex items-center gap-1"><i className="ri-time-line"></i>更新于 {post.date}</span>
              <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{post.views} 围观</span>
              <span className="flex items-center gap-1"><i className="ri-book-open-line"></i>阅读约 {post.minutes} 分钟</span>
            </div>
          </header>

          {/* 封面 */}
          <figure className="mt-6 rounded-3xl overflow-hidden">
            <img
              src={post.cover}
              alt={`${post.title} - 九一吃瓜现场速递`}
              title={post.title}
              className="w-full h-72 md:h-[440px] object-cover object-top"
            />
            <figcaption className="px-1 pt-2 text-xs text-foreground-400">
              现场画面示意 · 图：{post.author}
            </figcaption>
          </figure>

          {/* 正文 */}
          <div className="mt-8 space-y-5 text-[15px] md:text-base text-foreground-700 leading-8">
            {post.content.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>

          {/* 分享 */}
          <div className="mt-8 flex flex-wrap items-center gap-3 pt-6 border-t border-background-200">
            <span className="text-sm text-foreground-500">觉得这瓜不错？分享出去：</span>
            <button aria-label="分享到微信" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-secondary-500 hover:text-background-50 transition">
              <i className="ri-wechat-line"></i>
            </button>
            <button aria-label="分享到微博" className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-accent-500 hover:text-background-50 transition">
              <i className="ri-weibo-line"></i>
            </button>
            <button aria-label="复制链接" onClick={() => navigator.clipboard?.writeText(window.location.href)} className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-500 hover:text-background-50 transition">
              <i className="ri-link"></i>
            </button>
          </div>

          {/* 相关推荐 */}
          <div className="mt-10">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-5">
              你可能还想围观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {rec.map((p) => (
                <Link
                  key={p.id}
                  to={`/post/${p.slug}`}
                  className="group rounded-2xl border border-background-200 bg-background-50 overflow-hidden hover:border-primary-200 transition"
                >
                  <div className="h-28 w-full overflow-hidden">
                    <img
                      src={p.cover}
                      alt={p.title}
                      title={p.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-3.5">
                    <span className="text-xs text-primary-600">{p.category}</span>
                    <h3 className="mt-1 text-sm font-bold text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition">
                      {p.title}
                    </h3>
                    <div className="mt-2 flex items-center gap-3 text-xs text-foreground-400">
                      <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{p.views}</span>
                      <span className="flex items-center gap-1"><i className="ri-fire-line text-primary-500"></i>{p.hot}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* 侧栏 */}
        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-24 space-y-5">
            <div className="rounded-3xl border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950 mb-4">
                <i className="ri-fire-fill text-primary-500"></i> 大家都在看
              </h2>
              <div className="space-y-1">
                {[...POSTS].sort((a, b) => b.hot - a.hot).slice(0, 5).map((p, i) => (
                  <Link key={p.id} to={`/post/${p.slug}`} className="flex items-start gap-3 rounded-xl p-2 hover:bg-background-100 transition">
                    <span className={`w-7 h-7 shrink-0 flex items-center justify-center rounded-lg font-heading ${i < 3 ? "text-accent-600" : "text-foreground-300"}`}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-sm font-medium text-foreground-800 leading-snug line-clamp-2">{p.title}</span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border border-primary-100 bg-gradient-to-br from-primary-50 to-background-50 p-5">
              <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950 mb-3">
                <i className="ri-megaphone-line text-primary-500"></i> 发现新鲜瓜？
              </h2>
              <p className="text-sm text-foreground-600 mb-4">拍到一手现场？欢迎投稿，让更多人前排围观。</p>
              <a
                href="#contact"
                onClick={(e) => { e.preventDefault(); document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }); }}
                className="block text-center rounded-full bg-primary-500 text-background-50 text-sm font-medium py-2.5 hover:bg-primary-600 transition whitespace-nowrap"
              >
                我要投稿
              </a>
            </div>

            {/* 栏目快捷 */}
            <div className="rounded-3xl border border-secondary-200 bg-secondary-50 p-5">
              <h2 className="text-base font-bold text-foreground-950 mb-3">热门栏目</h2>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((c) => (
                  <Link
                    key={c.id}
                    to="/"
                    className="inline-flex items-center gap-1.5 rounded-full bg-background-50 text-foreground-700 text-xs px-3 py-1.5 hover:bg-secondary-500 hover:text-background-50 transition whitespace-nowrap"
                  >
                    <i className={c.icon}></i> {c.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </aside>
      </div>
    </article>
  );
}